import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  Card,
  Button,
  message,
  Spin,
  Alert,
  Typography,
  Space,
  Tag,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  BankOutlined,
  LinkOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { getRentPaymentStatus } from "../lib/payments/stripe";
import Logo from "../components/general/Logo";

const { Title, Text, Paragraph } = Typography;

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 2rem 1rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const StatusCard = styled(Card)`
  border-radius: 16px;
  border: 2px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  background: white;
  margin-bottom: 1.5rem;

  .ant-card-body {
    padding: 2rem;
  }
`;

const StatusIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin: 0 auto 1.5rem auto;

  .anticon {
    font-size: 40px;
  }

  &.success {
    background: rgba(34, 197, 94, 0.1);
    color: #22c55e;
  }

  &.processing {
    background: rgba(245, 158, 11, 0.1);
    color: #f59e0b;
  }

  &.verification {
    background: rgba(30, 58, 138, 0.1);
    color: #1e3a8a;
  }

  &.error {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }
`;

const PaymentReturn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const rentPaymentId = searchParams.get("rentPaymentId");

  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const pollingIntervalRef = useRef(null);
  const [pollingCount, setPollingCount] = useState(0);
  const MAX_POLLING_ATTEMPTS = 12; // 12 attempts * 5 seconds = 60 seconds max

  const fetchStatus = useCallback(async () => {
    if (!rentPaymentId) {
      setLoading(false);
      return;
    }

    try {
      const response = await getRentPaymentStatus(rentPaymentId);
      // Backend may return { success: true, data: {...} } or just the data object
      const data = response?.data || response;
      setStatusData(data);
      return data;
    } catch (error) {
      console.error("[PaymentReturn] Error fetching status:", error);
      message.error("Failed to load payment status");
      return null;
    } finally {
      setLoading(false);
    }
  }, [rentPaymentId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Start polling if status is pending_verification or processing
  useEffect(() => {
    if (!statusData) return;

    const shouldPoll =
      statusData?.status === "pending_verification" ||
      statusData?.status === "processing";

    if (!shouldPoll) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    if (pollingIntervalRef.current) return;

    let attempts = 0;
    pollingIntervalRef.current = setInterval(async () => {
      attempts++;
      setPollingCount(attempts);

      if (attempts >= MAX_POLLING_ATTEMPTS) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        return;
      }

      const updatedResponse = await fetchStatus();
      if (updatedResponse) {
        // Extract status from response (may be wrapped in data)
        const updatedStatus = updatedResponse?.data || updatedResponse;
        const status = updatedStatus?.status;
        // Stop polling if status changed to succeeded or failed
        if (
          status === "succeeded" ||
          status === "failed" ||
          status === "payment_failed"
        ) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
      }
    }, 5000); // Poll every 5 seconds

    // Cleanup on unmount or when status changes
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [statusData, fetchStatus]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchStatus();
      message.success("Status updated");
    } catch (error) {
      message.error("Failed to refresh status");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Content>
          <Spin
            size="large"
            style={{ display: "block", textAlign: "center", marginTop: "4rem" }}
          />
        </Content>
      </Container>
    );
  }

  if (!rentPaymentId) {
    return (
      <Container>
        <Content>
          <StatusCard>
            <Alert
              message="Invalid Payment"
              description="No payment ID provided in the URL."
              type="error"
              showIcon
            />
            <Button
              type="primary"
              onClick={() => navigate("/")}
              style={{ marginTop: "1rem" }}
            >
              Go Home
            </Button>
          </StatusCard>
        </Content>
      </Container>
    );
  }

  if (!statusData) {
    return (
      <Container>
        <Content>
          <StatusCard>
            <Alert
              message="Payment Not Found"
              description="The payment you're looking for could not be found."
              type="error"
              showIcon
            />
            <Button
              type="primary"
              onClick={() => navigate("/")}
              style={{ marginTop: "1rem" }}
            >
              Go Home
            </Button>
          </StatusCard>
        </Content>
      </Container>
    );
  }

  // Determine UI state based on backend status (canonical source of truth)
  const backendStatus = statusData?.status;
  const verificationUrl =
    statusData?.hostedVerificationUrl || statusData?.hosted_verification_url;
  const nextAction = statusData?.next_action;

  let statusConfig = {
    icon: <ClockCircleOutlined />,
    title: "Checking payment status...",
    description: "Please wait while we check your payment status.",
    type: "processing",
    showVerificationButton: false,
    showRetryButton: false,
  };

  if (backendStatus === "succeeded") {
    statusConfig = {
      icon: <CheckCircleOutlined />,
      title: "Payment Successful",
      description: "Your rent payment has been processed successfully.",
      type: "success",
      showVerificationButton: false,
      showRetryButton: false,
    };
  } else if (
    backendStatus === "pending_verification" ||
    backendStatus === "created" ||
    (nextAction?.type === "verify_with_microdeposits" && verificationUrl)
  ) {
    statusConfig = {
      icon: <BankOutlined />,
      title: "Verify your bank account",
      description:
        "Your bank requires verification via microdeposits. Stripe will send two small deposits (less than $1 each) to your bank account. These typically appear within 1-2 business days. Check your bank statement, then return to Stripe's verification page to enter the amounts.",
      type: "verification",
      showVerificationButton: true,
      showRetryButton: false,
    };
  } else if (backendStatus === "processing") {
    statusConfig = {
      icon: <ClockCircleOutlined />,
      title: "Payment processing",
      description:
        "ACH payments can take a few business days to complete. You'll receive a confirmation email once the payment is processed.",
      type: "processing",
      showVerificationButton: false,
      showRetryButton: false,
    };
  } else if (
    backendStatus === "requires_payment_method" ||
    backendStatus === "payment_failed" ||
    backendStatus === "failed"
  ) {
    statusConfig = {
      icon: <ExclamationCircleOutlined />,
      title: "Payment Failed",
      description:
        statusData?.error ||
        "Your payment could not be processed. Please try again with a different payment method.",
      type: "error",
      showVerificationButton: false,
      showRetryButton: true,
    };
  }

  return (
    <Container>
      <Header>
        <Logo size="large" displayMode="both" color="black" linkTo={null} />
        <Text type="secondary">Payment Status</Text>
      </Header>

      <Content>
        <StatusCard>
          <StatusIcon className={statusConfig.type}>
            {statusConfig.icon}
          </StatusIcon>

          <Title
            level={3}
            style={{ textAlign: "center", marginBottom: "1rem" }}
          >
            {statusConfig.title}
          </Title>

          <Paragraph
            style={{
              textAlign: "center",
              fontSize: "16px",
              color: "#666",
              marginBottom: "2rem",
            }}
          >
            {statusConfig.description}
          </Paragraph>

          {statusConfig.showVerificationButton && verificationUrl && (
            <>
              <Button
                type="primary"
                size="large"
                icon={
                  <LinkOutlined style={{ filter: "brightness(0) invert(1)" }} />
                }
                onClick={() => {
                  window.open(verificationUrl, "_blank", "noopener,noreferrer");
                }}
                style={{
                  width: "100%",
                  height: "48px",
                  backgroundColor: "var(--color-primary)",
                  borderColor: "var(--color-primary)",
                  marginBottom: "1rem",
                }}
              >
                Verify now
              </Button>
              <Text
                type="secondary"
                style={{
                  display: "block",
                  textAlign: "center",
                  fontSize: "14px",
                  marginBottom: "1.5rem",
                }}
              >
                After you verify the microdeposits, your payment will process
                automatically. ACH payments typically take 1-3 business days to
                complete.
              </Text>
            </>
          )}

          {statusConfig.showRetryButton && (
            <Button
              type="primary"
              size="large"
              onClick={() => navigate(`/rent-payment/${rentPaymentId}`)}
              style={{
                width: "100%",
                height: "48px",
                marginBottom: "1rem",
              }}
            >
              Try Again
            </Button>
          )}

          <Space
            direction="vertical"
            style={{ width: "100%", marginTop: "1.5rem" }}
          >
            {statusData?.amount && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text strong>Payment Amount:</Text>
                <Text>${statusData.amount}</Text>
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text strong>Status:</Text>
              <Tag
                color={
                  statusConfig.type === "success"
                    ? "success"
                    : statusConfig.type === "error"
                    ? "error"
                    : "processing"
                }
              >
                {backendStatus === "succeeded"
                  ? "Paid"
                  : backendStatus === "processing"
                  ? "Processing"
                  : backendStatus === "pending_verification"
                  ? "Verification Required"
                  : backendStatus === "payment_failed"
                  ? "Failed"
                  : backendStatus || "Unknown"}
              </Tag>
            </div>
            {statusData?.paymentIntentId && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text strong>Payment ID:</Text>
                <Text code style={{ fontSize: "12px" }}>
                  {statusData.paymentIntentId.slice(0, 20)}...
                </Text>
              </div>
            )}
          </Space>

          <Button
            type="default"
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={refreshing}
            style={{ width: "100%", marginTop: "1.5rem" }}
          >
            Refresh Status
          </Button>

          <Button
            type="link"
            onClick={() => navigate("/")}
            style={{ width: "100%", marginTop: "0.5rem" }}
          >
            Back to Home
          </Button>
        </StatusCard>
      </Content>
    </Container>
  );
};

export default PaymentReturn;
